/* eslint-disable @angular-eslint/no-empty-lifecycle-method */
import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    inject,
    OnDestroy,
    PLATFORM_ID,
    signal,
    ViewChild,
    afterNextRender,
    effect,
    Output,
    EventEmitter,
    input,
    AfterViewInit,
    Injector,
} from '@angular/core';

import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { SwiperContainer } from 'swiper/element/bundle';
import { Swiper, SwiperOptions } from 'swiper/types';

@Component({
    selector: 'iea-slider',
    templateUrl: './slider.component.html',
    styleUrls: [`./slider.component.scss`],
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [NgOptimizedImage],
    standalone: true,
})
export default class SliderComponent implements OnDestroy, AfterViewInit {
    private readonly platformId = inject(PLATFORM_ID);
    isBrowser = isPlatformBrowser(this.platformId);

    @ViewChild('thumbsSwiperRef')
    thumbsSwiperRef: ElementRef<SwiperContainer> | undefined;
    thumbsSwiper?: Swiper;

    @ViewChild('viewerSwiperRef')
    viewerSwiperRef: ElementRef<SwiperContainer> | undefined;
    viewerSwiper?: Swiper;

    dir = input<'ltr' | 'rtl'>('ltr');

    active = input(undefined);

    @Output() activeChange = new EventEmitter<{
        index: number;
        artBoard: any;
    }>();
    viewerSlideChange(swiper: any) {
        this.activeChange.emit({
            index: swiper.activeIndex,
            artBoard: this.slides[swiper.activeIndex],
        });
    }

    slides = input([]);
    initialized = signal(false);
    injector = inject(Injector);
    constructor() {
        afterNextRender(() => {
            effect(
                () => {
                    const active = this.active();
                    if (active == null) return;
                    if (this.thumbsSwiper) this.thumbsSwiper.slideTo(active);
                    if (this.viewerSwiper) this.viewerSwiper.slideTo(active);
                },
                { injector: this.injector }
            );

            effect(
                () => {
                    const slides = this.slides();
                    setTimeout(() => {
                        if (this.thumbsSwiper) this.thumbsSwiper.update();
                        if (this.viewerSwiper) this.viewerSwiper.update();
                    }, 100);
                },
                { injector: this.injector }
            );
        });
    }

    ngOnDestroy(): void {
        this.viewerSwiper?.destroy();
        this.thumbsSwiper?.destroy();
    }

    async ngAfterViewInit() {
        if (this.isBrowser) {
            const m = await import('swiper/element/bundle');

            m.register();
            this.initialized.set(true);

            this._init();
        }
    }
    private _init() {
        this.thumbsSwiper = this.initThumbsSwiper();
        this.viewerSwiper = this.initViewerSwiper();
    }

    initViewerSwiper() {
        const conf = {
            ...viewerConfig,

            on: {
                init: (swiper: Swiper) => {
                    swiper.slideTo(this.active());
                },
                slideChange: (swiper: Swiper) => {
                    if (!this.thumbsSwiper) return;
                    if (swiper.activeIndex !== this.thumbsSwiper.activeIndex) this.thumbsSwiper.slideTo(swiper.activeIndex);
                    this.viewerSlideChange(swiper);
                },
            },
        } as SwiperOptions;

        return makeSwiper(this.viewerSwiperRef, conf);
    }

    initThumbsSwiper() {
        const thumbsConf = {
            ...thumbsConfig,
            on: {
                init: (swiper: Swiper) => {
                    swiper.slideTo(this.active());
                },
                slideChange: (swiper: Swiper) => {
                    if (!this.viewerSwiper) return;
                    if (swiper.activeIndex !== this.viewerSwiper.activeIndex) this.viewerSwiper.slideTo(swiper.activeIndex);
                },
            },
        };
        return makeSwiper(this.thumbsSwiperRef, thumbsConf);
    }
}
function makeSwiper(ref: ElementRef<SwiperContainer>, conf: SwiperOptions) {
    Object.assign(ref.nativeElement, conf);
    ref.nativeElement.initialize();
    return ref.nativeElement.swiper;
}
const viewerConfig: SwiperOptions = {
    init: false,
    spaceBetween: 60,
    direction: 'horizontal',
    loop: false,
    slidesPerView: 1,
    grabCursor: true,
    zoom: true,
    keyboard: {
        enabled: true,
    },
    navigation: true,
    effect: 'creative',
    creativeEffect: {
        perspective: true,
        limitProgress: 1,
        progressMultiplier: 1.125,
        prev: {
            translate: [0, 0, -400],
            scale: 0,
        },
        next: {
            translate: ['100%', 0, 0],
        },
    },
    breakpoints: {
        768: {
            slidesPerView: 1,
            spaceBetween: 10,
        },
    },
};

const thumbsConfig: SwiperOptions = {
    grabCursor: true,
    spaceBetween: 30,
    centeredSlides: true,
    slidesPerView: 5,
    direction: 'horizontal',
    loop: false,
    watchSlidesProgress: true,
};
